/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { FC } from "react"
import { noop } from "lodash"
import { Dropdown, Button, Menu } from "@duxton-web/ui"
import { ChevronDownUniversal } from "@duxton-web/icons"
import { useDispatch, useSelector } from "react-redux"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"
import { useTranslation } from "common/utils/i18n"
import { TroyWebTracker } from "utils/tracking"
import { EventAction, EventCategory, EventEntryPoint } from "constants/tracking"
import {
  dropdownSubtextStyle,
  dropdownMenuItemStyle,
  dropdownTextStyle,
  dropdownButton,
} from "./dropdownStyles"

interface CategoryItemDropdownProps {
  disabled?: boolean
}

export const AddItemMenu: FC = () => {
  const dispatch = useDispatch()

  const {
    categories,
    overview: { category },
    isEditAllowed,
    isMartStore,
    merchantID,
    menuGroupDetail: { menuGroupID },
    merchantGroupID,
  } = useSelector((state: AppState) => state.menu)

  const hasCategories = categories.length > 0

  const onClickItem = () => {
    TroyWebTracker.sendEvent({
      category: EventCategory.menuOverviewTab,
      action: EventAction.clickAddItem,
      labels: {
        ...(merchantID
          ? { STORE_ID: merchantID }
          : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
        ENTRY_POINT: EventEntryPoint.addNewDropdown,
      },
    })

    return hasCategories && isEditAllowed
      ? dispatch(
          actionCreator.menu.toggleDrawer({
            item: {
              categoryID: category?.categoryID,
            },
            showItem: true,
            sourceShowItem: EventCategory.menuOverviewModifyItem,
          }),
        )
      : noop
  }

  return (
    <div onClick={onClickItem}>
      <span css={dropdownTextStyle}>
        {useTranslation("menu.overview.dropdowns.item")}
      </span>
      <p css={dropdownSubtextStyle}>
        {useTranslation(
          isMartStore
            ? "catalogue.overview.dropdowns.itemBody"
            : "menu.overview.dropdowns.itemBody",
        )}
      </p>
    </div>
  )
}

export const AddCategoryMenu: FC = () => {
  const dispatch = useDispatch()

  const {
    overview: { section },
    isEditAllowed,
    isMartStore,
    merchantID,
    menuGroupDetail: { menuGroupID },
    merchantGroupID,
  } = useSelector((state: AppState) => state.menu)

  const onClickCategory = () => {
    TroyWebTracker.sendEvent({
      category: EventCategory.menuOverviewTab,
      action: EventAction.clickAddCategory,
      labels: merchantID
        ? { STORE_ID: merchantID }
        : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID },
    })

    return isEditAllowed
      ? dispatch(
          actionCreator.menu.toggleDrawer({
            category: {
              sectionID: section?.sectionID,
            },
            showCategory: true,
          }),
        )
      : noop
  }

  return (
    <div onClick={onClickCategory}>
      <span css={dropdownTextStyle}>
        {useTranslation("menu.overview.dropdowns.category")}
      </span>
      <p css={dropdownSubtextStyle}>
        {useTranslation(
          isMartStore
            ? "catalogue.overview.dropdowns.categoryBody"
            : "menu.overview.dropdowns.categoryBody",
        )}
      </p>
    </div>
  )
}

export const CategoryItemDropdown: FC<CategoryItemDropdownProps> = ({
  disabled,
}: CategoryItemDropdownProps) => {
  const { categories, isEditAllowed } = useSelector(
    (state: AppState) => state.menu,
  )

  const hasCategories = categories.length > 0

  return (
    <Dropdown
      disabled={disabled || !isEditAllowed}
      trigger={["click"]}
      overlay={
        <Menu
          css={css`
            width: 270px;
          `}
        >
          <Menu.Item
            key="item"
            css={dropdownMenuItemStyle}
            disabled={!hasCategories || !isEditAllowed}
          >
            <AddItemMenu />
          </Menu.Item>
          <Menu.Item
            key="category"
            css={dropdownMenuItemStyle}
            disabled={!isEditAllowed}
          >
            <AddCategoryMenu />
          </Menu.Item>
        </Menu>
      }
    >
      <Button css={dropdownButton} shape="default">
        {useTranslation("menu.overview.buttons.addNew")}
        <ChevronDownUniversal />
      </Button>
    </Dropdown>
  )
}
